import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../../../../../auth/AuthContext";
import { useAvailableFunds } from "../../../../../hooks/use-available-funds";
import {
  applyCepMask,
  applyCnpjMask,
  cleanCnpj,
} from "../../../../../utils/format-utils";
import { companyService } from "../services/company-service";
import { Company } from "../types";
import {
  CompanyRegistrationFormData,
  EditCredentialsFormData,
  companyRegistrationSchema,
  editCredentialsSchema,
} from "../utils/validation-schemas";

export const companyKeys = {
  all: ["companies"] as const,
  list: (dbName: string) => [...companyKeys.all, "list", dbName] as const,
};

export function useCompanyRegistration() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: funds = [] } = useAvailableFunds();
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  const xamaDbName = funds.find((item: any) => item.value === "xama")?.key;

  const registrationForm = useForm<CompanyRegistrationFormData>({
    resolver: zodResolver(companyRegistrationSchema),
    defaultValues: {
      cnpj: "",
      razaoSocial: "",
      cep: "",
      endereco: "",
      apelido: "",
      emailContato: "",
      franquiaUuid: "",
      redutor: 1,
      nfsAtivo: false,
      nfsSrAtivo: false,
    },
  });

  const editForm = useForm<
    EditCredentialsFormData,
    any,
    EditCredentialsFormData
  >({
    resolver: zodResolver(editCredentialsSchema),
    defaultValues: {
      apelido: "",
      emailContato: "",
      nfsAtivo: false,
      nfsSrAtivo: false,
      franquiaUuid: "",
      redutor: 1,
    },
  });

  const {
    data: companiesData = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: companyKeys.list(xamaDbName || ""),
    queryFn: async () => {
      if (!xamaDbName) return [];
      const response = await companyService.getCompanies({
        dbName: "xama",
      });

      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data.map((empresa) => ({
          id: String(empresa.id),
          cnpj: applyCnpjMask(empresa.cnpj),
          razaoSocial: empresa.razao_social,
          cep: applyCepMask(empresa.cep),
          endereco: empresa.endereco,
          apelido: empresa.ops,
          emailContato: empresa.email_contato,
          criadoPor: empresa.created_by,
          dataCriacao: new Date(empresa.created_at).toLocaleDateString("pt-BR"),
          franquiaUuid: empresa.franquia_uuid || "",
          formato: empresa.formato,
          nfsAtivo: empresa.nfs_ativo ?? false,
          nfsSrAtivo: empresa.nfs_sr_ativo ?? false,
          redutor: empresa.redutor !== undefined && empresa.redutor !== null ? parseFloat(empresa.redutor.toString()) : 1,
        }));
      }
      return [];
    },
    enabled: !!xamaDbName,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 10,
  });

  const registerMutation = useMutation({
    mutationFn: async (data: CompanyRegistrationFormData) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Missing required data");
      }

      return companyService.registerCompany({
        type: "cadastro",
        dbName: xamaDbName,
        cnpj: cleanCnpj(data.cnpj),
        razaoSocial: data.razaoSocial,
        cep: data.cep.replace(/\D/g, ""),
        endereco: data.endereco,
        apelido: data.apelido,
        emailContato: data.emailContato,
        franquiaUuid: data.franquiaUuid,
        redutor: data.redutor !== undefined ? Number(data.redutor) : 1,
        nfsAtivo: data.nfsAtivo,
        nfsSrAtivo: data.nfsSrAtivo,
        criadoPor: user.email || user.name || "",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyKeys.list(xamaDbName || ""),
      });
      registrationForm.reset();
      refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: EditCredentialsFormData;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Dados faltantes");
      }

      return companyService.updateCompanyCredentials({
        type: "update",
        dbName: xamaDbName,
        id: Number(companyId),
        apelido: data.apelido,
        emailContato: data.emailContato,
        nfsAtivo: data.nfsAtivo,
        nfsSrAtivo: data.nfsSrAtivo,
        franquiaUuid: data.franquiaUuid,
        redutor: data.redutor !== undefined ? Number(data.redutor) : 1,
        criadoPor: user.email || user.name || "",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyKeys.list(xamaDbName || ""),
      });
      setEditingCompany(null);
      editForm.reset();
      refetch();
    },
  });

  const handleRegister = registrationForm.handleSubmit((data) => {
    registerMutation.mutate(data);
  });

  const handleEditClick = (company: Company) => {
    setEditingCompany(company);
    editForm.reset({
      apelido: company.apelido || "",
      emailContato: company.emailContato || "",
      nfsAtivo: company.nfsAtivo ?? false,
      nfsSrAtivo: company.nfsSrAtivo ?? false,
      franquiaUuid: company.franquiaUuid || "",
      redutor: company.redutor !== undefined && company.redutor !== null ? company.redutor : 1,
    });
  };

  const handleUpdate = editForm.handleSubmit((data) => {
    if (!editingCompany) return;
    updateMutation.mutate({
      companyId: editingCompany.id,
      data,
    });
  });

  const handleCancelEdit = () => {
    setEditingCompany(null);
    editForm.reset();
  };

  return {
    registrationForm,
    editForm,
    companies: companiesData,
    isLoading,
    isRegistering: registerMutation.isPending,
    isUpdating: updateMutation.isPending,
    editingCompany,
    xamaDbName,
    handleRegister,
    handleEditClick,
    handleUpdate,
    handleCancelEdit,
    registerError: registerMutation.error,
    updateError: updateMutation.error,
    registerSuccess: registerMutation.isSuccess,
    updateSuccess: updateMutation.isSuccess,
  };
}
